import { useQuery } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { 
  LineChart, BarChart, Bar, Line, XAxis, YAxis, CartesianGrid, 
  Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell 
} from "recharts";
import { 
  TrendingUp, BookOpen, Tag, Calendar, Clock, 
  Award, BarChart3, FileText 
} from "lucide-react";

interface AnalyticsData {
  totalEntries: number;
  moodTrends: {
    trends: Array<{ date: string; mood: number }>;
    distribution: Array<{ mood: string; count: number }>;
  };
  writingPatterns: {
    entriesPerDay: Array<{ date: string; count: number }>;
    wordsPerDay: Array<{ date: string; words: number }>;
    peakHours: Array<{ hour: string; count: number }>;
    entriesByDayOfWeek: Array<{ day: string; count: number }>;
  };
  tagAnalysis: {
    topTags: Array<{ tag: string; count: number }>;
    totalTags: number;
    totalTagUsage: number;
  };
  streaks: {
    current: number;
    longest: number;
  };
  contentInsights: {
    totalWords: number;
    avgWords: number;
    mostProductiveDay: string;
    mostProductiveTime: string;
    avgEntriesPerWeek: number;
  };
}

const COLORS = ['#3b82f6', '#8b5cf6', '#ec4899', '#f59e0b', '#10b981', '#06b6d4', '#6366f1', '#f43f5e', '#14b8a6', '#a855f7'];

const MOOD_COLORS: Record<string, string> = {
  'happy': '#10b981',
  'excited': '#f59e0b',
  'grateful': '#8b5cf6',
  'joyful': '#ec4899',
  'content': '#3b82f6',
  'calm': '#06b6d4',
  'peaceful': '#14b8a6',
  'relaxed': '#6366f1',
  'neutral': '#9ca3af',
  'okay': '#d1d5db',
  'sad': '#6b7280',
  'anxious': '#ef4444',
  'worried': '#f97316',
  'stressed': '#dc2626',
  'angry': '#991b1b',
  'frustrated': '#dc2626',
  'upset': '#b91c1c',
  'depressed': '#7f1d1d',
};

export default function JournalAnalytics() {
  const { data: analytics, isLoading } = useQuery<AnalyticsData>({
    queryKey: ["/api/journal/analytics"]
  });

  if (isLoading) {
    return (
      <div className="w-full p-6 space-y-6">
        <Skeleton className="h-12 w-64" />
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {[1, 2, 3, 4].map((i) => (
            <Skeleton key={i} className="h-32" />
          ))}
        </div>
        <Skeleton className="h-96" />
        <Skeleton className="h-96" />
      </div>
    );
  }

  if (!analytics || analytics.totalEntries === 0) {
    return (
      <div className="w-full p-6">
        <div className="text-center py-12">
          <FileText className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
          <h2 className="text-xl font-semibold mb-2">No Analytics Available</h2>
          <p className="text-muted-foreground">Start writing journal entries to see your analytics.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full p-6 space-y-6" data-testid="journal-analytics">
      <div className="flex items-center gap-3">
        <BarChart3 className="h-8 w-8 text-primary" />
        <h1 className="text-3xl font-bold" data-testid="text-analytics-title">Journal Analytics</h1>
      </div>

      {/* Overview Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card data-testid="card-total-entries">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Entries</CardTitle>
            <BookOpen className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold" data-testid="text-total-entries">{analytics.totalEntries}</div>
            <p className="text-xs text-muted-foreground mt-1">
              {analytics.contentInsights.avgEntriesPerWeek} per week avg
            </p>
          </CardContent>
        </Card>

        <Card data-testid="card-current-streak">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Current Streak</CardTitle>
            <Award className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold" data-testid="text-current-streak">{analytics.streaks.current} days</div>
            <p className="text-xs text-muted-foreground mt-1">
              Longest: {analytics.streaks.longest} days
            </p>
          </CardContent>
        </Card>

        <Card data-testid="card-total-words">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Words</CardTitle>
            <TrendingUp className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold" data-testid="text-total-words">
              {analytics.contentInsights.totalWords.toLocaleString()}
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              {analytics.contentInsights.avgWords} words per entry
            </p>
          </CardContent>
        </Card>

        <Card data-testid="card-unique-tags">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Unique Tags</CardTitle>
            <Tag className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold" data-testid="text-unique-tags">{analytics.tagAnalysis.totalTags}</div>
            <p className="text-xs text-muted-foreground mt-1">
              {analytics.tagAnalysis.totalTagUsage} total uses
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Mood Trends */}
      {analytics.moodTrends.trends.length > 0 && (
        <Card data-testid="card-mood-trends">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <TrendingUp className="h-5 w-5" />
              Mood Trends (Last 30 Days)
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={analytics.moodTrends.trends}>
                <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                <XAxis 
                  dataKey="date" 
                  className="text-xs"
                  tickFormatter={(date) => new Date(date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                />
                <YAxis 
                  className="text-xs"
                  domain={[0, 5]}
                  ticks={[1, 2, 3, 4, 5]}
                />
                <Tooltip 
                  labelFormatter={(date) => new Date(date).toLocaleDateString()}
                  formatter={(value: number) => {
                    const moods = ['😢', '😟', '😐', '😊', '😄'];
                    return [`${value} ${moods[Math.floor(value) - 1] || '😐'}`, 'Mood Score'];
                  }}
                />
                <Legend />
                <Line 
                  type="monotone" 
                  dataKey="mood" 
                  stroke="#3b82f6" 
                  strokeWidth={2}
                  dot={{ fill: '#3b82f6' }}
                  name="Mood Score"
                />
              </LineChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      )}

      {/* Mood Distribution & Writing Patterns Side by Side */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Mood Distribution */}
        {analytics.moodTrends.distribution.length > 0 && (
          <Card data-testid="card-mood-distribution">
            <CardHeader>
              <CardTitle>Mood Distribution</CardTitle>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={analytics.moodTrends.distribution}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ mood, percent }) => `${mood} (${(percent * 100).toFixed(0)}%)`}
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="count"
                  >
                    {analytics.moodTrends.distribution.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={MOOD_COLORS[entry.mood.toLowerCase()] || COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        )}

        {/* Entries by Day of Week */}
        <Card data-testid="card-entries-by-day">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Calendar className="h-5 w-5" />
              Entries by Day of Week
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={analytics.writingPatterns.entriesByDayOfWeek}>
                <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                <XAxis dataKey="day" className="text-xs" />
                <YAxis className="text-xs" />
                <Tooltip />
                <Bar dataKey="count" fill="#8b5cf6" radius={[8, 8, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>

      {/* Writing Patterns - Entries Per Day */}
      <Card data-testid="card-entries-per-day">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5" />
            Entries Per Day (Last 7 Days)
          </CardTitle>
        </CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={analytics.writingPatterns.entriesPerDay}>
              <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
              <XAxis 
                dataKey="date" 
                className="text-xs"
                tickFormatter={(date) => new Date(date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
              />
              <YAxis className="text-xs" />
              <Tooltip 
                labelFormatter={(date) => new Date(date).toLocaleDateString()}
                formatter={(value) => [value, 'Entries']}
              />
              <Bar dataKey="count" fill="#10b981" radius={[8, 8, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      {/* Words Per Day */}
      {analytics.writingPatterns.wordsPerDay.length > 0 && (
        <Card data-testid="card-words-per-day">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <TrendingUp className="h-5 w-5" />
              Words Per Day (Last 30 Days)
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={analytics.writingPatterns.wordsPerDay}>
                <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                <XAxis 
                  dataKey="date" 
                  className="text-xs"
                  tickFormatter={(date) => new Date(date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                />
                <YAxis className="text-xs" />
                <Tooltip 
                  labelFormatter={(date) => new Date(date).toLocaleDateString()}
                  formatter={(value) => [value, 'Words']}
                />
                <Line 
                  type="monotone" 
                  dataKey="words" 
                  stroke="#f59e0b" 
                  strokeWidth={2}
                  dot={{ fill: '#f59e0b' }}
                />
              </LineChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      )}

      {/* Tag Analysis */}
      {analytics.tagAnalysis.topTags.length > 0 && (
        <Card data-testid="card-top-tags">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Tag className="h-5 w-5" />
              Top Tags
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={analytics.tagAnalysis.topTags} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                <XAxis type="number" className="text-xs" />
                <YAxis dataKey="tag" type="category" width={100} className="text-xs" />
                <Tooltip />
                <Bar dataKey="count" fill="#ec4899" radius={[0, 8, 8, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      )}

      {/* Peak Writing Times */}
      {analytics.writingPatterns.peakHours.length > 0 && (
        <Card data-testid="card-peak-hours">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Clock className="h-5 w-5" />
              Peak Writing Hours
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={analytics.writingPatterns.peakHours}>
                <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                <XAxis dataKey="hour" className="text-xs" />
                <YAxis className="text-xs" />
                <Tooltip />
                <Bar dataKey="count" fill="#06b6d4" radius={[8, 8, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      )}

      {/* Content Insights Summary */}
      <Card data-testid="card-content-insights">
        <CardHeader>
          <CardTitle>Content Insights</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="space-y-1">
              <p className="text-sm text-muted-foreground">Most Productive Day</p>
              <p className="text-2xl font-bold" data-testid="text-productive-day">
                {analytics.contentInsights.mostProductiveDay}
              </p>
            </div>
            <div className="space-y-1">
              <p className="text-sm text-muted-foreground">Most Productive Time</p>
              <p className="text-2xl font-bold" data-testid="text-productive-time">
                {analytics.contentInsights.mostProductiveTime}
              </p>
            </div>
            <div className="space-y-1">
              <p className="text-sm text-muted-foreground">Average Entry Length</p>
              <p className="text-2xl font-bold" data-testid="text-avg-words">
                {analytics.contentInsights.avgWords} words
              </p>
            </div>
            <div className="space-y-1">
              <p className="text-sm text-muted-foreground">Entries Per Week</p>
              <p className="text-2xl font-bold" data-testid="text-entries-per-week">
                {analytics.contentInsights.avgEntriesPerWeek}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
